import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Target, Phone, Mail, Clock, AlertTriangle, TrendingUp,
  Loader2, RefreshCw, ChevronDown, ChevronUp, CheckCircle,
  ClipboardList, Users, Zap, ArrowRight, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const PRIORITE_COLORS = {
  haute: 'bg-red-100 text-red-700 border-red-200',
  moyenne: 'bg-amber-100 text-amber-700 border-amber-200',
  basse: 'bg-blue-100 text-blue-700 border-blue-200',
};

const TYPE_ICONS = {
  relance_urgente: AlertTriangle,
  relance: Clock,
  conversion_urgente: Zap,
  conversion: TrendingUp,
};

export function ClosingTab() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingRelance, setGeneratingRelance] = useState(null);

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const res = await api.get('/crm-vente-logiciel/closing-suggestions');
      setSuggestions(res.data?.suggestions || []);
    } catch {
      toast.error('Erreur chargement suggestions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSuggestions(); }, []);

  const hautes = suggestions.filter(s => s.priorite === 'haute');
  const moyennes = suggestions.filter(s => s.priorite === 'moyenne');

  return (
    <div className="space-y-4" data-testid="closing-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Target className="w-5 h-5 text-red-500" />
            Aide au Closing IA
          </h3>
          <p className="text-sm text-muted-foreground">Valentine analyse tes prospects et te donne des actions concrètes</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadSuggestions} disabled={loading} data-testid="btn-refresh-closing">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Actualiser
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
      ) : suggestions.length === 0 ? (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <p className="font-semibold text-emerald-800">Aucune action de closing en attente</p>
            <p className="text-sm text-emerald-600 mt-1">Tous tes prospects sont bien suivis !</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-4 text-center">
                <p className="text-2xl font-bold text-red-600">{hautes.length}</p>
                <p className="text-xs text-red-500">Urgentes</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-4 text-center">
                <p className="text-2xl font-bold text-amber-600">{moyennes.length}</p>
                <p className="text-xs text-amber-500">Moyennes</p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="pt-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">{suggestions.reduce((a, s) => a + (s.prix || 0), 0)}€</p>
                <p className="text-xs text-emerald-500">MRR potentiel</p>
              </CardContent>
            </Card>
          </div>

          {/* Suggestions */}
          <ScrollArea className="h-[500px]">
            <div className="space-y-3 pr-3">
              {suggestions.map((s, i) => {
                const Icon = TYPE_ICONS[s.type] || Target;
                return (
                  <Card key={i} className={`border-l-4 ${s.priorite === 'haute' ? 'border-l-red-500' : 'border-l-amber-400'}`} data-testid={`closing-suggestion-${i}`}>
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${s.priorite === 'haute' ? 'bg-red-100' : 'bg-amber-100'}`}>
                          <Icon className={`w-4 h-4 ${s.priorite === 'haute' ? 'text-red-600' : 'text-amber-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-sm">{s.nom}</p>
                            <Badge className={`text-[10px] ${PRIORITE_COLORS[s.priorite]}`}>{s.priorite}</Badge>
                            {s.prix > 0 && <Badge variant="outline" className="text-[10px]">{s.prix}€/mois</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground">{s.suggestion}</p>
                          <div className="mt-2 p-2 bg-gray-50 rounded-lg border">
                            <p className="text-xs font-medium text-gray-600 mb-1">Action suggérée :</p>
                            <p className="text-sm">{s.action_proposee}</p>
                          </div>
                          <div className="flex gap-2 mt-2">
                            {s.email && (
                              <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => window.open(`mailto:${s.email}`)}>
                                <Mail className="w-3 h-3 mr-1" />Email
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="text-xs h-7">
                              <Phone className="w-3 h-3 mr-1" />Appeler
                            </Button>
                            <Button
                              size="sm"
                              className="text-xs h-7 bg-emerald-600 hover:bg-emerald-700"
                              disabled={generatingRelance === s.prospect_id}
                              onClick={async () => {
                                setGeneratingRelance(s.prospect_id);
                                try {
                                  const res = await api.post(`/visio/relance-auto/${s.prospect_id}`);
                                  if (res.data?.success) {
                                    toast.success(res.data.message);
                                  }
                                } catch (e) {
                                  toast.error('Erreur génération relance');
                                } finally {
                                  setGeneratingRelance(null);
                                }
                              }}
                              data-testid={`btn-relance-${i}`}
                            >
                              {generatingRelance === s.prospect_id
                                ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                : <Send className="w-3 h-3 mr-1" />}
                              Relance auto
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </ScrollArea>
        </>
      )}
    </div>
  );
}


export function OnboardingTab() {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const loadChecklists = async () => {
    setLoading(true);
    try {
      const res = await api.get('/crm-vente-logiciel/onboarding-checklists');
      setChecklists(res.data?.checklists || []);
    } catch {
      toast.error('Erreur chargement checklists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadChecklists(); }, []);

  const toggleTache = async (checklistId, tacheId) => {
    try {
      const res = await api.put(`/crm-vente-logiciel/onboarding/${checklistId}/tache/${tacheId}`);
      if (res.data?.success) {
        setChecklists(prev => prev.map(cl => {
          if (cl.id !== checklistId) return cl;
          return {
            ...cl,
            progression: res.data.progression,
            nb_faites: res.data.nb_faites,
            statut: res.data.statut,
            taches: cl.taches.map(t => t.id === tacheId ? { ...t, fait: !t.fait } : t),
          };
        }));
        if (res.data.statut === 'termine') toast.success('Onboarding terminé !');
      }
    } catch {
      toast.error('Erreur mise à jour');
    }
  };

  return (
    <div className="space-y-4" data-testid="onboarding-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-emerald-600" />
            Onboarding Clients
          </h3>
          <p className="text-sm text-muted-foreground">Checklists auto-générées quand un prospect devient client</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadChecklists} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Actualiser
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
      ) : checklists.length === 0 ? (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6 text-center">
            <Users className="w-12 h-12 text-blue-400 mx-auto mb-3" />
            <p className="font-semibold text-blue-800">Aucun onboarding en cours</p>
            <p className="text-sm text-blue-600 mt-1">Les checklists apparaîtront automatiquement quand un prospect deviendra client.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {checklists.map(cl => {
            const isExpanded = expandedId === cl.id;
            const etapes = [...new Set(cl.taches.map(t => t.etape))];
            return (
              <Card key={cl.id} data-testid={`checklist-${cl.id}`}>
                <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : cl.id)}>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-sm flex items-center gap-2">
                        {cl.statut === 'termine' ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <ArrowRight className="w-4 h-4 text-blue-500" />}
                        {cl.client_nom}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground">{cl.client_email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-sm font-bold">{cl.progression}%</p>
                        <p className="text-[10px] text-muted-foreground">{cl.nb_faites}/{cl.nb_taches}</p>
                      </div>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </div>
                  <Progress value={cl.progression} className="h-1.5 mt-2" />
                </CardHeader>
                {isExpanded && (
                  <CardContent className="pt-0">
                    {etapes.map(etape => (
                      <div key={etape} className="mb-3">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">{etape}</p>
                        <div className="space-y-1.5">
                          {cl.taches.filter(t => t.etape === etape).map(t => (
                            <div key={t.id} className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                              <Checkbox
                                checked={t.fait}
                                onCheckedChange={() => toggleTache(cl.id, t.id)}
                                className="mt-0.5"
                                data-testid={`tache-${t.id}`}
                              />
                              <div className="flex-1">
                                <p className={`text-sm ${t.fait ? 'line-through text-muted-foreground' : 'font-medium'}`}>{t.titre}</p>
                                <p className="text-xs text-muted-foreground">{t.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
